import {
  US_STATE_FIPS_BY_CODE,
  type UsStateCode,
} from '@act/shared/data-transfer-objects';
import { geoCentroid, geoPath, geoMercator } from 'd3-geo';
import { feature } from 'topojson-client';
import { useEffect, useState } from 'react';
import type { Feature, FeatureCollection } from 'geojson';
import type {
  GeometryCollection,
  Topology,
} from 'topojson-specification';

/** SVG path and centroid bundle for one state's counties. */
export interface StateCountyGeoBundle {
  paths: Array<{ name: string; d: string }>;
  centroids: Record<string, [number, number]>;
  viewBox: string;
}

const MAP_WIDTH = 100;
const MAP_HEIGHT = 140;

const isUsStateCode = (code: string): code is UsStateCode =>
  code.length === 2 && code in US_STATE_FIPS_BY_CODE;

type CountiesTopology = Topology<{ counties: GeometryCollection }>;

/**
 * Projects state county TopoJSON into SVG paths and label centroids.
 * @param usStateCode - Two-letter state code.
 * @returns Geo bundle or null when the state is unknown or has no counties.
 */
const buildStateCountyGeo = async (
  usStateCode: string,
): Promise<StateCountyGeoBundle | null> => {
  const normalized = usStateCode.trim().toUpperCase();
  if (!isUsStateCode(normalized)) {
    return null;
  }

  const stateFips = US_STATE_FIPS_BY_CODE[normalized];
  const topology = (await import('us-atlas/counties-10m.json'))
    .default as unknown as CountiesTopology;
  const collection = feature(
    topology,
    topology.objects.counties,
  ) as FeatureCollection;

  const stateFeatures = collection.features.filter(featureItem => {
    const id = String(featureItem.id ?? '');
    return id.length >= 2 && id.startsWith(stateFips);
  });

  if (stateFeatures.length === 0) {
    return null;
  }

  const stateCollection: FeatureCollection = {
    type: 'FeatureCollection',
    features: stateFeatures,
  };

  const projection = geoMercator().fitSize(
    [MAP_WIDTH, MAP_HEIGHT],
    stateCollection,
  );
  const pathGenerator = geoPath(projection);

  const paths: StateCountyGeoBundle['paths'] = [];
  const centroids: StateCountyGeoBundle['centroids'] = {};

  for (const countyFeature of stateFeatures) {
    const name =
      typeof countyFeature.properties?.name === 'string'
        ? countyFeature.properties.name
        : '';
    if (!name) {
      continue;
    }
    const pathD = pathGenerator(countyFeature);
    if (!pathD) {
      continue;
    }
    paths.push({ name, d: pathD });
    const centroid = geoCentroid(countyFeature as Feature);
    const projected = projection(centroid);
    if (projected) {
      centroids[name] = [projected[0], projected[1]];
    }
  }

  return {
    paths,
    centroids,
    viewBox: `0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`,
  };
};

/**
 * Loads and memoizes county SVG geometry for a US state.
 * @param usStateCode - Two-letter state code from the report customer or snapshot.
 * @returns Geo bundle, loading flag, and error state.
 */
export const useStateCountyGeo = (
  usStateCode: string | null | undefined,
): {
  geo: StateCountyGeoBundle | null;
  loading: boolean;
} => {
  const [geo, setGeo] = useState<StateCountyGeoBundle | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const code = usStateCode?.trim().toUpperCase();
    if (!code) {
      setGeo(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    void buildStateCountyGeo(code)
      .then(bundle => {
        if (!cancelled) {
          setGeo(bundle);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGeo(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [usStateCode]);

  return { geo, loading };
};
